import { type FC, useMemo, useState } from 'react'
import { Flag, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { generateKeyFromName } from '@/lib/utils'
import { TargetingRuleBuilder } from './TargetingRuleBuilder'
import { UserSearchSelect } from './UserSearchSelect'
import type {
    CreateFeatureFlagRequest,
    DevicePlatform,
    FeatureFlag,
    FeatureFlagCategory,
    FeatureFlagGroup,
    FeatureFlagTargetType,
    LogicalOperator,
    TargetRule,
    TargetableField,
} from '@/types/feature-flag'

const CATEGORIES: FeatureFlagCategory[] = [
    'CORE',
    'PREMIUM',
    'BETA',
    'EXPERIMENTAL',
]

const TARGET_TYPES: Array<{
    value: FeatureFlagTargetType
    label: string
    hint: string
}> = [
    {
        value: 'ALL',
        label: 'Everyone',
        hint: 'On for every user who passes the client gates.',
    },
    {
        value: 'SPECIFIC',
        label: 'Specific users',
        hint: 'Only the user IDs you list.',
    },
    {
        value: 'GROUP',
        label: 'Groups',
        hint: 'Members of the selected audiences.',
    },
    {
        value: 'RULE',
        label: 'Rules',
        hint: 'Users matching the conditions you build.',
    },
    {
        value: 'PERCENTAGE',
        label: 'Percentage rollout',
        hint: 'A sticky random slice of users.',
    },
]

const PLATFORMS: DevicePlatform[] = ['ANDROID', 'IOS']

interface CreateEditFlagModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit: (payload: CreateFeatureFlagRequest) => void
    flag?: FeatureFlag | null
    groups: FeatureFlagGroup[]
    allFlags: FeatureFlag[]
    fields: TargetableField[]
    isSubmitting?: boolean
}

const buildInitialState = (flag?: FeatureFlag | null) => ({
    key: flag?.key ?? '',
    name: flag?.name ?? '',
    description: flag?.description ?? '',
    category: flag?.category ?? ('CORE' as FeatureFlagCategory),
    isActive: flag?.status === 'ACTIVE',
    targetType: flag?.targetType ?? ('ALL' as FeatureFlagTargetType),
    targetUserIds: flag?.targetUserIds ?? [],
    targetGroupIds: flag?.targetGroups?.map(group => group.id) ?? [],
    targetRules: flag?.targetRules ?? [],
    targetRuleOperator: flag?.targetRuleOperator ?? ('AND' as LogicalOperator),
    rolloutPercentage: flag?.rolloutPercentage ?? 10,
    platforms: flag?.platforms ?? [],
    minAppVersion: flag?.minAppVersion ?? '',
    maxAppVersion: flag?.maxAppVersion ?? '',
    dependencies: flag?.dependencies ?? [],
})

export const CreateEditFlagModal: FC<CreateEditFlagModalProps> = ({
    open,
    onOpenChange,
    onSubmit,
    flag,
    groups,
    allFlags,
    fields,
    isSubmitting = false,
}) => {
    const isEditing = Boolean(flag)
    // The parent remounts this component on every open (see its `key`), so the
    // initial state is always built from the flag being edited.
    const [state, setState] = useState(() => buildInitialState(flag))

    /** Once the key is edited by hand, the name stops driving it. */
    const [keyEditedManually, setKeyEditedManually] = useState(false)
    const [groupSearch, setGroupSearch] = useState('')

    const visibleGroups = useMemo(() => {
        const term = groupSearch.trim().toLowerCase()
        if (!term) return groups

        return groups.filter(
            group =>
                group.name.toLowerCase().includes(term) ||
                group.key.toLowerCase().includes(term) ||
                (group.description ?? '').toLowerCase().includes(term)
        )
    }, [groups, groupSearch])

    const set = <K extends keyof typeof state>(
        field: K,
        value: (typeof state)[K]
    ) => setState(prev => ({ ...prev, [field]: value }))

    const handleNameChange = (name: string) => {
        setState(prev => ({
            ...prev,
            name,
            // The key is permanent once saved, so it only tracks the name while
            // creating — and only until someone types their own.
            key:
                isEditing || keyEditedManually
                    ? prev.key
                    : generateKeyFromName(name),
        }))
    }

    const dependencyOptions = useMemo(
        () => allFlags.filter(candidate => candidate.id !== flag?.id),
        [allFlags, flag?.id]
    )

    const canSubmit =
        state.key.trim().length > 1 &&
        state.name.trim().length > 1 &&
        !isSubmitting

    const handleSubmit = () => {
        if (!canSubmit) return

        const payload: CreateFeatureFlagRequest = {
            key: state.key.trim(),
            name: state.name.trim(),
            description: state.description.trim() || undefined,
            status: state.isActive ? 'ACTIVE' : 'INACTIVE',
            category: state.category,
            targetType: state.targetType,
            platforms: state.platforms,
            minAppVersion: state.minAppVersion.trim() || undefined,
            maxAppVersion: state.maxAppVersion.trim() || undefined,
            dependencies: state.dependencies,
        }

        // Only send the configuration the chosen target type actually uses, so
        // switching types doesn't drag stale targeting along with it.
        if (state.targetType === 'SPECIFIC') {
            payload.targetUserIds = state.targetUserIds
        }
        if (state.targetType === 'GROUP') {
            payload.targetGroupIds = state.targetGroupIds
        }
        if (state.targetType === 'RULE') {
            payload.targetRules = state.targetRules
            payload.targetRuleOperator = state.targetRuleOperator
        }
        if (state.targetType === 'PERCENTAGE') {
            payload.rolloutPercentage = state.rolloutPercentage
        }

        onSubmit(payload)
    }

    const activeTargetType = TARGET_TYPES.find(
        type => type.value === state.targetType
    )

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl sm:max-w-2xl sm:rounded-2xl"
            >
                <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white">
                    <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <Flag className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    {isEditing
                                        ? 'Edit feature flag'
                                        : 'New feature flag'}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {isEditing
                                        ? 'Changes reach the app on its next refresh.'
                                        : 'New flags start off until you turn them on.'}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Plain overflow rather than a scroll primitive: this sits
                        in a flex column, where a viewport-based scroller needs
                        an explicit height to scroll at all. */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="space-y-5 px-6 py-5">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="flag-name">Name</Label>
                                    <Input
                                        id="flag-name"
                                        value={state.name}
                                        placeholder="Vault withdrawals"
                                        onChange={event =>
                                            handleNameChange(event.target.value)
                                        }
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="flag-key">Key</Label>
                                    <Input
                                        id="flag-key"
                                        value={state.key}
                                        disabled={isEditing}
                                        placeholder="vault_withdrawals"
                                        className="font-mono"
                                        onChange={event => {
                                            setKeyEditedManually(true)
                                            set(
                                                'key',
                                                generateKeyFromName(
                                                    event.target.value
                                                )
                                            )
                                        }}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {isEditing
                                            ? 'The app compiles against this — it cannot change.'
                                            : keyEditedManually
                                              ? 'Permanent once saved.'
                                              : 'Generated from the name. Edit it if you want something else.'}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="flag-description">
                                    Description
                                </Label>
                                <Textarea
                                    id="flag-description"
                                    value={state.description}
                                    placeholder="What does this flag control?"
                                    onChange={event =>
                                        set('description', event.target.value)
                                    }
                                />
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <Select
                                        value={state.category}
                                        onValueChange={value =>
                                            set(
                                                'category',
                                                value as FeatureFlagCategory
                                            )
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(category => (
                                                <SelectItem
                                                    key={category}
                                                    value={category}
                                                >
                                                    {category}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-end justify-between rounded-lg border border-border px-4 py-2.5">
                                    <div>
                                        <p className="text-sm font-medium text-[#0F172A]">
                                            Enabled
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Off means off for everyone.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={state.isActive}
                                        onCheckedChange={checked =>
                                            set('isActive', checked)
                                        }
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Who gets it</Label>
                                <Select
                                    value={state.targetType}
                                    onValueChange={value =>
                                        set(
                                            'targetType',
                                            value as FeatureFlagTargetType
                                        )
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TARGET_TYPES.map(type => (
                                            <SelectItem
                                                key={type.value}
                                                value={type.value}
                                            >
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    {activeTargetType?.hint}
                                </p>
                            </div>

                            {state.targetType === 'SPECIFIC' && (
                                <div className="space-y-2">
                                    <Label>Users</Label>
                                    <UserSearchSelect
                                        selectedIds={state.targetUserIds}
                                        onChange={ids =>
                                            set('targetUserIds', ids)
                                        }
                                    />
                                </div>
                            )}

                            {state.targetType === 'GROUP' && (
                                <div className="space-y-2">
                                    <Label>Groups</Label>
                                    {groups.length === 0 ? (
                                        <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                                            No groups yet — create one on the
                                            Groups tab.
                                        </p>
                                    ) : (
                                        <div className="space-y-2">
                                            {groups.length > 5 && (
                                                <div className="relative">
                                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                    <Input
                                                        value={groupSearch}
                                                        placeholder="Search groups"
                                                        className="pl-9"
                                                        onChange={event =>
                                                            setGroupSearch(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}

                                            <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border p-3">
                                                {visibleGroups.length === 0 ? (
                                                    <p className="py-2 text-center text-sm text-muted-foreground">
                                                        No groups match "
                                                        {groupSearch}"
                                                    </p>
                                                ) : (
                                                    visibleGroups.map(group => (
                                                        <label
                                                            key={group.id}
                                                            className="flex items-center gap-2 text-sm"
                                                        >
                                                            <Checkbox
                                                                checked={state.targetGroupIds.includes(
                                                                    group.id
                                                                )}
                                                                onCheckedChange={checked =>
                                                                    set(
                                                                        'targetGroupIds',
                                                                        checked
                                                                            ? [
                                                                                  ...state.targetGroupIds,
                                                                                  group.id,
                                                                              ]
                                                                            : state.targetGroupIds.filter(
                                                                                  id =>
                                                                                      id !==
                                                                                      group.id
                                                                              )
                                                                    )
                                                                }
                                                            />
                                                            <span>
                                                                {group.name}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {group.type ===
                                                                'STATIC'
                                                                    ? `${group.userIds.length} users`
                                                                    : 'rule-based'}
                                                            </span>
                                                        </label>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {state.targetType === 'RULE' && (
                                <div className="space-y-2">
                                    <Label>Rules</Label>
                                    <TargetingRuleBuilder
                                        rules={state.targetRules}
                                        onChange={(rules: TargetRule[]) =>
                                            set('targetRules', rules)
                                        }
                                        ruleOperator={state.targetRuleOperator}
                                        onRuleOperatorChange={operator =>
                                            set('targetRuleOperator', operator)
                                        }
                                        fields={fields}
                                    />
                                </div>
                            )}

                            {state.targetType === 'PERCENTAGE' && (
                                <div className="space-y-2">
                                    <Label htmlFor="flag-rollout">
                                        Rollout: {state.rolloutPercentage}%
                                    </Label>
                                    <input
                                        id="flag-rollout"
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={state.rolloutPercentage}
                                        className="w-full accent-[#17A2B8]"
                                        onChange={event =>
                                            set(
                                                'rolloutPercentage',
                                                Number(event.target.value)
                                            )
                                        }
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Buckets are sticky: raising the number
                                        only adds users, it never reshuffles who
                                        already has the feature.
                                    </p>
                                </div>
                            )}

                            <div className="space-y-3 rounded-xl border border-border p-4">
                                <div>
                                    <p className="text-sm font-medium text-[#0F172A]">
                                        Client limits
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Applied on top of targeting. Clients
                                        that don't report a platform or version
                                        fail these checks.
                                    </p>
                                </div>

                                <div className="flex gap-4">
                                    {PLATFORMS.map(platform => (
                                        <label
                                            key={platform}
                                            className="flex items-center gap-2 text-sm"
                                        >
                                            <Checkbox
                                                checked={state.platforms.includes(
                                                    platform
                                                )}
                                                onCheckedChange={checked =>
                                                    set(
                                                        'platforms',
                                                        checked
                                                            ? [
                                                                  ...state.platforms,
                                                                  platform,
                                                              ]
                                                            : state.platforms.filter(
                                                                  value =>
                                                                      value !==
                                                                      platform
                                                              )
                                                    )
                                                }
                                            />
                                            {platform}
                                        </label>
                                    ))}
                                    <span className="text-xs text-muted-foreground">
                                        {state.platforms.length === 0
                                            ? 'All platforms'
                                            : ''}
                                    </span>
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="flag-min-version">
                                            Min app version
                                        </Label>
                                        <Input
                                            id="flag-min-version"
                                            value={state.minAppVersion}
                                            placeholder="1.2.0"
                                            onChange={event =>
                                                set(
                                                    'minAppVersion',
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="flag-max-version">
                                            Max app version
                                        </Label>
                                        <Input
                                            id="flag-max-version"
                                            value={state.maxAppVersion}
                                            placeholder="2.0.0"
                                            onChange={event =>
                                                set(
                                                    'maxAppVersion',
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {dependencyOptions.length > 0 && (
                                <div className="space-y-2">
                                    <Label>Depends on</Label>
                                    <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border p-3">
                                        {dependencyOptions.map(option => (
                                            <label
                                                key={option.id}
                                                className="flex items-center gap-2 text-sm"
                                            >
                                                <Checkbox
                                                    checked={state.dependencies.includes(
                                                        option.key
                                                    )}
                                                    onCheckedChange={checked =>
                                                        set(
                                                            'dependencies',
                                                            checked
                                                                ? [
                                                                      ...state.dependencies,
                                                                      option.key,
                                                                  ]
                                                                : state.dependencies.filter(
                                                                      key =>
                                                                          key !==
                                                                          option.key
                                                                  )
                                                        )
                                                    }
                                                />
                                                <span className="font-mono text-xs">
                                                    {option.key}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        This flag stays off unless every flag
                                        above is also on for the user.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t px-6 py-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-[#17A2B8] hover:bg-[#148c9e]"
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                        >
                            {isSubmitting
                                ? 'Saving...'
                                : isEditing
                                  ? 'Save changes'
                                  : 'Create flag'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
